import type { SqliteOptions } from './sqlite-options';
import type { QueryResult } from './query-result';
import type { SqliteProvider } from './sqlite.provider';
import { SqliteConfigurationError } from '../../errors';

/**
 * A stable SqliteProvider wrapper whose underlying delegate can be swapped at runtime.
 *
 * All DI-injected repository singletons are created once and hold a reference to this
 * proxy.  When the database is closed and re-initialized (e.g. after an idle-timeout
 * lock) only the delegate needs to be updated via `setDelegate()` – the existing
 * singletons automatically route to the new provider without needing to be recreated.
 */
export class ProxySqliteProvider implements SqliteProvider {
  private delegate: SqliteProvider | null = null;
  executeObjects?: <TRow extends Record<string, unknown> = Record<string, unknown>>(
    sql: string,
    params?: unknown[],
  ) => Promise<TRow[]>;

  setDelegate(provider: SqliteProvider): void {
    this.delegate = provider;

    const executeObjects = provider.executeObjects;
    if (executeObjects) {
      this.executeObjects = async <TRow extends Record<string, unknown> = Record<string, unknown>>(
        sql: string,
        params?: unknown[],
      ): Promise<TRow[]> => {
        return executeObjects(sql, params);
      };
      return;
    }

    delete this.executeObjects;
  }

  hasDelegate(): boolean {
    return this.delegate !== null;
  }

  private get(): SqliteProvider {
    if (!this.delegate) throw new SqliteConfigurationError('Worker not initialized. Call initialize() first.');

    return this.delegate;
  }

  async initialize(options: SqliteOptions): Promise<void> {
    return this.get().initialize(options);
  }

  async execute(sql: string, params?: unknown[]): Promise<QueryResult[]> {
    return this.get().execute(sql, params);
  }

  async export(): Promise<Uint8Array> {
    return this.get().export();
  }

  async import(data: Uint8Array): Promise<void> {
    return this.get().import(data);
  }

  async getRowsModified(): Promise<number> {
    return this.get().getRowsModified();
  }

  async close(): Promise<void> {
    return this.get().close();
  }

  async beginTransaction(): Promise<void> {
    return this.get().beginTransaction();
  }

  async commit(): Promise<void> {
    return this.get().commit();
  }

  async rollback(): Promise<void> {
    return this.get().rollback();
  }
}
